import React, { Component, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import i18n from '@/lib/i18n/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  /** True if error is likely from a failed chunk load (e.g. back after deploy, 404 → HTML as JS). */
  static isChunkLoadError(error: Error): boolean {
    const msg = (error?.message || "").toLowerCase();
    return (
      msg.includes("unexpected token '<'") ||
      msg.includes("loading chunk") ||
      msg.includes("loading css chunk") ||
      msg.includes("dynamically imported module") ||
      msg.includes("importing a module script failed") ||
      msg.includes("failed to fetch dynamically imported module")
    );
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    this.setState({ error, errorInfo });
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    // One-time auto-reload on chunk load error (e.g. back button after deploy).
    // Uses localStorage + TTL instead of sessionStorage because Safari mobile
    // clears sessionStorage on every reload → infinite refresh loop.
    if (
      this.state.hasError &&
      this.state.error &&
      ErrorBoundary.isChunkLoadError(this.state.error) &&
      !prevState.hasError
    ) {
      const key = "rakb_chunk_error_reload_ts";
      const now = Date.now();
      const stored = localStorage.getItem(key);
      const lastReload = stored ? parseInt(stored, 10) : 0;

      // Allow one auto-reload per 30 s; after that show the error UI to break the loop.
      if (!stored || now - lastReload > 30_000) {
        localStorage.setItem(key, String(now));
        window.location.reload();
        return;
      }
      // Second failure within 30 s → stop reloading, clean up and show error UI.
      localStorage.removeItem(key);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    // Utiliser window.location pour la redirection dans ErrorBoundary
    // car c'est un composant de classe et ne peut pas utiliser useNavigate
    window.location.href = '/';
  };

  handleReload = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    // Pour ErrorBoundary, un reload peut être nécessaire en cas d'erreur critique
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = this.state.error && ErrorBoundary.isChunkLoadError(this.state.error);

      return (
        <div className="min-h-screen bg-scandi-bg flex items-center justify-center p-6 sm:p-12">
          <div className="max-w-xl w-full">
            <div className="bg-white rounded-[2rem] shadow-xl shadow-green-night/5 border border-border overflow-hidden">
              <div className="p-8 sm:p-12 text-center">
                <div className="mx-auto w-20 h-20 bg-emerald-soft rounded-3xl flex items-center justify-center mb-8 animate-in fade-in zoom-in duration-500">
                  <AlertCircle className="h-10 w-10 text-green-night" />
                </div>

                <h1 className="text-3xl font-bold text-text-primary tracking-tight mb-4">
                  {isChunkError ? "Page à recharger" : "Oups ! Quelque chose s'est mal passé"}
                </h1>

                <p className="text-text-secondary text-lg mb-10 leading-relaxed">
                  {isChunkError
                    ? "Une nouvelle version du site est disponible. Rechargez la page pour continuer (vos données ne sont pas perdues)."
                    : "Désolé pour ce contretemps. Une erreur inattendue est survenue, mais pas d'inquiétude, vos données sont en sécurité."}
                </p>

                {import.meta.env.DEV && this.state.error && (
                  <div className="mb-10 text-left bg-scandi-surfaceAlt/50 p-6 rounded-2xl border border-border overflow-hidden">
                    <p className="font-mono text-sm text-text-primary font-semibold truncate mb-2">
                      {this.state.error.name}: {this.state.error.message}
                    </p>
                    {this.state.errorInfo && (
                      <details className="group">
                        <summary className="cursor-pointer text-xs text-text-tertiary uppercase tracking-wider font-bold hover:text-green-night transition-colors list-none flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-night group-open:bg-destructive" />
                          Détails techniques
                        </summary>
                        <pre className="mt-4 text-[10px] text-text-secondary overflow-auto max-h-32 bg-white/50 p-3 rounded-lg border border-border/50 leading-relaxed">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </details>
                    )}
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  {isChunkError ? (
                    <>
                      <Button
                        onClick={this.handleReload}
                        className="h-14 rounded-2xl bg-green-night hover:bg-green-night/90 text-white font-semibold shadow-lg shadow-green-night/10 transition-all hover:scale-[1.02] active:scale-[0.98]"
                      >
                        <RefreshCw className="mr-2 h-5 w-5" />
                        {i18n.t("common.reloadPage")}
                      </Button>
                      <Button
                        onClick={this.handleReset}
                        variant="outline"
                        className="h-14 rounded-2xl border-border hover:bg-scandi-surface font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                      >
                        <Home className="mr-2 h-5 w-5" />
                        {i18n.t("common.backToHome")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={this.handleReset}
                        className="h-14 rounded-2xl bg-green-night hover:bg-green-night/90 text-white font-semibold shadow-lg shadow-green-night/10 transition-all hover:scale-[1.02] active:scale-[0.98]"
                      >
                        <Home className="mr-2 h-5 w-5" />
                        {i18n.t("common.backToHome")}
                      </Button>
                      <Button
                        onClick={this.handleReload}
                        variant="outline"
                        className="h-14 rounded-2xl border-border hover:bg-scandi-surface font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                      >
                        <RefreshCw className="mr-2 h-5 w-5" />
                        {i18n.t("common.retryPage")}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-scandi-surface border-t border-border p-6 text-center">
                <p className="text-sm text-text-secondary">
                  Besoin d'aide ? Notre{' '}
                  <a href="/contact" className="text-green-night font-semibold hover:underline underline-offset-4">
                    support client
                  </a>
                  {' '}est à votre disposition.
                </p>
              </div>
            </div>

            <p className="mt-8 text-center text-xs text-text-tertiary font-medium uppercase tracking-[0.2em]">
              © {new Date().getFullYear()} RAKB — All Rights Reserved
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

